import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useMemo, useCallback } from 'react';
import { Icon, type IconName } from '../../../ui';
import { PhaseEntry } from '../../../hooks/useGetOrdersFeed';

interface OrderPhaseBadgesProps {
    phases: Array<PhaseEntry>;
    orderId: string;
}

type PhaseTone = {
    bg: string;
    color: string;
};

/**
 * Phase chips for the Orders "All" table (ENG-2150): one grey track per
 * row. Occupied phases fill with the phase color and invert the label,
 * icon, and count onto it. Empty phases stay muted on the track.
 */
const OrderPhaseBadges = ({ phases, orderId }: OrderPhaseBadgesProps) => {
    const navigate = useNavigate();

    const phaseLabels = useMemo(() => ({
        'new-order': 'New',
        'in-quoting': 'Quoting',
        'ready-to-order': 'Ready',
        'ordered': 'Ordered'
    }), []);

    const phaseToStatus = useMemo((): Record<string, 'newParts' | 'quoting' | 'selection' | 'ordered'> => ({
        'new-order': 'newParts',
        'in-quoting': 'quoting',
        'ready-to-order': 'selection',
        'ordered': 'ordered'
    }), []);

    const phaseIcons = useMemo((): Record<string, IconName> => ({
        'new-order': 'plus',
        'in-quoting': 'message',
        'ready-to-order': 'check',
        'ordered': 'checkCircle'
    }), []);

    const phaseTones = useMemo((): Record<string, PhaseTone> => ({
        'new-order': { bg: 'brand.solid', color: 'fg.onBrand' },
        'in-quoting': { bg: 'fg.muted', color: 'fg.onBrand' },
        'ready-to-order': { bg: 'warning.fg', color: 'fg.onBrand' },
        'ordered': { bg: 'success.fg', color: 'fg.onBrand' },
    }), []);

    const handleBadgeClick = useCallback((phase: string, event: React.MouseEvent) => {
        event.stopPropagation();
        const status = phaseToStatus[phase] || 'newParts';
        navigate(`/order/${orderId}`, {
            state: { initialStatus: status, source: 'dashboard' }
        });
    }, [navigate, orderId, phaseToStatus]);

    return (
        <Flex display="inline-flex" align="center" bg="bg.subtle" borderRadius="lg" p="2px">
            {phases.map(p => {
                const iconName = phaseIcons[p.phase];
                const label = phaseLabels[p.phase as keyof typeof phaseLabels] || p.phase;
                const isEmpty = p.total === 0;
                const tone = phaseTones[p.phase] ?? { bg: 'fg.muted', color: 'fg.onBrand' };

                return (
                    <Flex
                        key={p.phase}
                        as="button"
                        type="button"
                        w="130px"
                        flexShrink={0}
                        py={1}
                        px={2}
                        align="center"
                        justify="center"
                        gap={1.5}
                        fontSize="xs"
                        fontWeight="semibold"
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={isEmpty ? 'transparent' : tone.color}
                        bg={isEmpty ? 'transparent' : tone.bg}
                        color={isEmpty ? 'fg.subtle' : tone.color}
                        cursor={isEmpty ? 'default' : 'pointer'}
                        onClick={(e) => handleBadgeClick(p.phase, e)}
                        position="relative"
                    >
                        {iconName && <Icon name={iconName} size="xs" />}
                        {label}
                        {p.total > 0 && (
                            <Text as="span" fontSize="xs" fontWeight="semibold" lineHeight="1">
                                {p.total}
                            </Text>
                        )}
                        {p.alert && (
                            <Tooltip label={p.alert} placement="top" hasArrow>
                                <Box
                                    position="absolute"
                                    top="-6px"
                                    right="-6px"
                                    bg="error.solid"
                                    color="fg.onBrand"
                                    borderRadius="full"
                                    w="16px"
                                    h="16px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    fontSize="xs"
                                    zIndex={1}
                                >
                                    <Icon name="alert" size="12px" />
                                </Box>
                            </Tooltip>
                        )}
                    </Flex>
                );
            })}
        </Flex>
    );
};

export default OrderPhaseBadges;
